import React, { useEffect } from 'react';
import { Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '@/shared/components/PageTitle';
import { useBreadcrumb } from '@/shared/lib/breadcrumb-context';
import { useNotification } from '@/shared/hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { usePostApiAdminGuides } from '@/features/guides/api/generated';
import { GuideForm } from '../components/GuideForm';
import { useGuideForm } from '../hooks/useGuideForm';
import { createGuidePayload } from '../utils/guideHelpers';

export const CreateGuidePage: React.FC = () => {
  const navigate = useNavigate();
  const { setItems } = useBreadcrumb();
  const { contextHolder, notification } = useNotification();
  const { t } = useTranslation();

  const {
    formState,
    isDirty,
    updateContentField,
    updateRootVehicleWithSelection,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
  } = useGuideForm();

  const { mutateAsync: createGuide, isPending: isCreating } = usePostApiAdminGuides();

  useEffect(() => {
    setItems([
      {
        title: (
          <span onClick={() => window.location.assign('/dashboard')} style={{ cursor: 'pointer' }}>
            Home
          </span>
        )
      },
      {
        title: (
          <span onClick={() => window.location.assign('/guides')} style={{ cursor: 'pointer' }}>
            {t('navigation.guides')}
          </span>
        )
      },
      { title: t('guides.createNew') },
    ]);

    return () => setItems(null);
  }, [setItems, t]);

  const handleCreate = async () => {
    if (!formState.content.problem.trim()) {
      notification.error(t('guides.validation.problemRequired'));
      return;
    }

    const payload = createGuidePayload(formState);

    try {
      const response = await createGuide({ data: payload });
      notification.success(t('guides.createSuccess'));
      if (response.guide?.id) {
        navigate(`/guides/${response.guide.id}`);
      } else {
        navigate('/guides');
      }
    } catch (error) {
      notification.error(t('guides.createError'));
    }
  };

  return (
    <>
      {contextHolder}
      <div>
        <PageTitle 
          title={t('guides.createNew')}
          subtitle={t('guides.createNewSubtitle')}
          level={4}
          extra={<Space>
            <Button onClick={() => navigate('/guides')}>{t('common.cancel')}</Button>
            <Button 
              type="primary" 
              loading={isCreating} 
              onClick={handleCreate} 
              disabled={!isDirty || isCreating}
            >
              {t('guides.create')}
            </Button>
          </Space>}
        />
        <GuideForm
          formData={formState}
          onUpdateContent={updateContentField}
          onUpdateRootVehicle={updateRootVehicleWithSelection}
          onAddSolution={addSolution}
          onUpdateSolution={updateSolution}
          onRemoveSolution={removeSolution}
          onReorderSolutions={reorderSolutions}
          onAddFilter={addFilter}
          onRemoveFilter={removeFilterAt}
          onUpdateFilter={updateFilterAt}
          mode="create"
        />
      </div>
    </>
  );
};